// ═══════════════════════════════════════════════════════════════════════════
// futuresContractHistory.js — deterministic normalization + contract-life
// percentile scoring for a MULTI-YEAR, SAME-CONTRACT-MONTH futures archive
// (Kevin, 2026-08-18 "GRAIN FUTURES/BASIS GAUGES" request item 1): "Score a
// current/selected forward contract against historical observations for the
// SAME contract month at approximately the SAME point in contract life. Do
// not use a generic continuous-nearby percentile as a substitute."
//
// WHY THIS IS A SEPARATE MODULE FROM grainContractTech (src/grainTargets.js):
// grainContractTech(rows).pctile is already a real, contract-specific
// percentile — but it is computed against ONE contract's OWN life-to-date
// closes only (futures_contract_history, keyed on one contract_sym, e.g.
// "CZ26"). It cannot answer "is $5.13 for May27 corn strong compared to what
// May corn has done historically at this same point before delivery" because
// it has no OTHER May contracts to compare against — only May27's own trail.
// That deeper archive is what Kevin's supplied WCC exports (Mar/May/Jul/Sep/
// Dec corn, ~2015-2026, Date/High/Low/Settle — docs/grain-data-notes.md) are
// FOR. Those files are not in this GitHub workspace as of 2026-08-18 (checked
// directly — see tools/check-futures-history.js's own header), so this file
// is the importer/normalizer/scoring SEAM, proven against deterministic
// fixtures, not a claim that real rows are wired in yet.
//
// CONTRACT-LIFE ALIGNMENT (stated explicitly, per the request's own
// instruction — this is a DATA-ALIGNMENT window, not an invented economic
// threshold): a row's "point in contract life" is measured as calendar days
// between its observation date and the 1st of its own delivery month
// (contractLifeOffsetDays — deliveryMonthStart minus obsDate; positive =
// before delivery, shrinking toward 0 as the contract approaches its
// delivery month). This is a COARSE, mechanical proxy for contract life — it
// is NOT the exchange's actual last-trading-day or first-notice-day, which
// vary by contract/exchange rule and are not sourced anywhere in this repo.
// If an exact CME expiration calendar is ever sourced, only this one
// function's internals need to change — every caller keeps working. Two
// historical observations are treated as "the same point in contract life"
// when their offsets are within `windowDays` of each other (default 15
// calendar days, a stated parameter — see scoreContractAtLifePoint).
//
// CONTRACT YEAR IS NEVER INFERRED. normalizeFuturesHistoryRow() requires an
// EXPLICIT contractYear on every row. A file spanning "2015-2026 daily
// Date/High/Low/Settle" for one contract month is genuinely ambiguous near
// each year's boundary — a January observation could belong to the tail of
// last year's contract or the start of this year's, and guessing either way
// is exactly the "invent a contract-month mapping for an ambiguous file"
// rule zero forbids. See docs/futures-history-import-spec.md for what a real
// import needs to supply.
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

import { opportunityCategory } from "./grainCurve";

// Standard CME contract month codes (exchange convention — the same table
// src/App.jsx documents inline as "standard exchange convention" for its own
// FUTURES_MONTH_CODES). Duplicated here by the same convention grainRules.js
// already follows for small, source-cited constant tables, not re-derived
// from App.jsx (App.jsx exports nothing).
const MONTH_NAME_TO_LETTER = {
  JAN: "F", FEB: "G", MAR: "H", APR: "J", MAY: "K", JUN: "M",
  JUL: "N", AUG: "Q", SEP: "U", OCT: "V", NOV: "X", DEC: "Z",
};
const LETTER_TO_MONTH_INDEX = { F: 0, G: 1, H: 2, J: 3, K: 4, M: 5, N: 6, Q: 7, U: 8, V: 9, X: 10, Z: 11 };
const VALID_LETTERS = new Set(Object.keys(LETTER_TO_MONTH_INDEX));

function resolveContractLetter(raw) {
  const s = String(raw || "").trim().toUpperCase();
  if (VALID_LETTERS.has(s)) return s;
  const byName = MONTH_NAME_TO_LETTER[s.slice(0, 3)];
  return byName || null;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// ── normalizeFuturesHistoryRow — validate/normalize ONE imported row. Never
// guesses a missing/ambiguous field; returns {ok:false, reason} instead so a
// bad row is counted and reported, never silently dropped or silently wrong
// (the same "unparsed rows are counted and reported, never skipped" rule
// CLAUDE.md states for check-soi.js).
// raw: { crop, contractMonth, contractYear, obsDate, settle, high, low, source }
export function normalizeFuturesHistoryRow(raw) {
  if (!raw || typeof raw !== "object") return { ok: false, reason: "row is not an object" };
  const crop = String(raw.crop || "").trim().toLowerCase();
  if (!crop) return { ok: false, reason: "missing crop" };

  const contractMonth = resolveContractLetter(raw.contractMonth);
  if (!contractMonth) return { ok: false, reason: `contractMonth "${raw.contractMonth}" is not a recognized CME month letter or name` };

  const contractYear = Number(raw.contractYear);
  if (!Number.isInteger(contractYear) || contractYear < 1990 || contractYear > 2100) {
    return { ok: false, reason: "contractYear is required and must be an explicit 4-digit year — never inferred from the observation date (ambiguous near calendar-year boundaries)" };
  }

  const obsDate = typeof raw.obsDate === "string" ? raw.obsDate.slice(0, 10) : null;
  if (!obsDate || !DATE_RE.test(obsDate)) return { ok: false, reason: `obsDate "${raw.obsDate}" is not a YYYY-MM-DD date` };

  const settle = Number(raw.settle);
  if (!Number.isFinite(settle) || settle <= 0) return { ok: false, reason: "settle is required and must be a positive number (Kevin: settlement is the primary historical field)" };

  let high = raw.high != null ? Number(raw.high) : null;
  let low = raw.low != null ? Number(raw.low) : null;
  if (high != null && !Number.isFinite(high)) high = null;
  if (low != null && !Number.isFinite(low)) low = null;
  if (high != null && low != null && high < low) {
    return { ok: false, reason: `high (${high}) is below low (${low}) — data quality issue, not imported` };
  }

  return {
    ok: true,
    row: { crop, contractMonth, contractYear, obsDate, settle, high, low, source: raw.source ? String(raw.source) : "unspecified-import" },
  };
}

// ── normalizeFuturesHistoryRows — batch form. Every row is counted; bad rows
// are reported with their reason, never silently skipped (bulk-import path).
export function normalizeFuturesHistoryRows(rawRows = []) {
  const rows = [], rejected = [];
  for (const raw of rawRows || []) {
    const r = normalizeFuturesHistoryRow(raw);
    if (r.ok) rows.push(r.row);
    else rejected.push({ raw, reason: r.reason });
  }
  return { rows, rejected, total: (rawRows || []).length };
}

// ── contractLifeOffsetDays — see header comment. Returns an integer number
// of calendar days between the observation and the 1st of the delivery
// month (UTC, to avoid a TZ off-by-one at midnight).
export function contractLifeOffsetDays({ contractMonth, contractYear, obsDate }) {
  const letter = resolveContractLetter(contractMonth);
  if (!letter || !Number.isInteger(Number(contractYear)) || !obsDate) return null;
  const monthIndex = LETTER_TO_MONTH_INDEX[letter];
  const deliveryStart = Date.UTC(Number(contractYear), monthIndex, 1);
  const obs = Date.UTC(...String(obsDate).slice(0, 10).split("-").map((x, i) => (i === 1 ? Number(x) - 1 : Number(x))));
  return Math.round((deliveryStart - obs) / 86400000);
}

// ── filterComparableFuturesHistory — the ONE comparability rule (same crop +
// same contract month + same approximate point in contract life + never the
// target's own contract year), extracted so a second caller (the regime-
// relative percentile below) reuses it instead of re-deriving a second
// comparability engine. scoreContractAtLifePoint is unchanged in behavior —
// this is a pure refactor, not a rule change.
export function filterComparableFuturesHistory({
  crop, contractMonth, targetContractYear, targetOffsetDays, history = [], windowDays = 15,
}) {
  const cropKey = String(crop || "").trim().toLowerCase();
  const letter = resolveContractLetter(contractMonth);
  return (history || []).filter((r) => {
    if (!r) return false;
    if (String(r.crop || "").trim().toLowerCase() !== cropKey) return false;
    if (resolveContractLetter(r.contractMonth) !== letter) return false;
    if (Number(r.contractYear) === Number(targetContractYear)) return false; // never compare a contract to itself
    const off = contractLifeOffsetDays(r);
    if (off == null || targetOffsetDays == null) return false;
    return Math.abs(off - targetOffsetDays) <= windowDays;
  });
}

// ── scoreContractAtLifePoint — the actual cross-year, same-point-in-life
// percentile. history: normalized rows (normalizeFuturesHistoryRow output),
// spanning MULTIPLE contract years for the same crop+contractMonth. Compares
// the TARGET observation's settle against every historical OTHER contract
// year's settle whose own contractLifeOffsetDays falls within windowDays of
// the target's offset. Requires both a minimum row count AND a minimum
// number of DISTINCT contract years represented (a single year's noisy few
// days should not carry a percentile on its own) — insufficient either way
// returns available:false with the real counts, never a manufactured read.
export function scoreContractAtLifePoint({
  crop, contractMonth, targetContractYear, targetOffsetDays, targetSettle,
  history = [], windowDays = 15, minSamples = 5, minContractYears = 3,
}) {
  if (targetOffsetDays == null || targetSettle == null || !Number.isFinite(targetSettle)) {
    return { available: false, reason: "no target observation to score", sampleSize: 0, yearsRepresented: 0, windowDays };
  }
  const comparable = filterComparableFuturesHistory({ crop, contractMonth, targetContractYear, targetOffsetDays, history, windowDays });
  const cropKey = String(crop || "").trim().toLowerCase();
  const letter = resolveContractLetter(contractMonth);

  const years = new Set(comparable.map((r) => Number(r.contractYear)));
  if (comparable.length < minSamples || years.size < minContractYears) {
    return {
      available: false,
      reason: comparable.length
        ? `only ${comparable.length} historical observation(s) across ${years.size} contract year(s) within ±${windowDays}d of this point in contract life (need ${minSamples} samples across ${minContractYears} years)`
        : `no historical ${cropKey || "?"} ${letter || "?"} contract-month archive imported within ±${windowDays}d of this point in contract life`,
      sampleSize: comparable.length,
      yearsRepresented: years.size,
      windowDays,
    };
  }

  const atOrBelow = comparable.filter((r) => Number(r.settle) <= targetSettle).length;
  const percentile = Math.round((atOrBelow / comparable.length) * 100);
  return {
    available: true,
    percentile,
    sampleSize: comparable.length,
    yearsRepresented: years.size,
    windowDays,
    comparableYears: [...years].sort((a, b) => a - b),
  };
}

// ── buildContractLifeFuturesGauge — gauge-shaped wrapper. Same 3-way
// opportunityCategory() labeling grainCurve.js already uses (one methodology,
// not a second invented scale). LIMITED/UNKNOWN when the archive can't
// defensibly say anything — never a manufactured percentile from a thin
// cross-year sample.
export function buildContractLifeFuturesGauge(score, contractLabel = null) {
  if (!score || !score.available) {
    return {
      state: "unknown", fillPct: null,
      label: "insufficient contract-month history to score this point in contract life",
      reason: (score && score.reason) || "no score computed",
      sampleSize: (score && score.sampleSize) || 0,
      yearsRepresented: (score && score.yearsRepresented) || 0,
      windowDays: (score && score.windowDays) ?? null,
      contractLabel,
    };
  }
  const cat = opportunityCategory(score.percentile / 100);
  return {
    state: "known", fillPct: score.percentile,
    label: `${cat.toUpperCase()} · ${score.percentile}th percentile (n=${score.sampleSize} · ${score.yearsRepresented} yrs · ±${score.windowDays}d)`,
    category: cat,
    sampleSize: score.sampleSize, yearsRepresented: score.yearsRepresented,
    windowDays: score.windowDays, comparableYears: score.comparableYears || [],
    contractLabel,
  };
}

// ── rankContractAmongYears — the SAME comparable population as
// scoreContractAtLifePoint (identical filterComparableFuturesHistory call, so
// the two can never disagree about WHICH observations are comparable), read
// one observation per YEAR instead of one per BAR.
//
// WHY A SECOND READING OF THE SAME ROWS EXISTS. scoreContractAtLifePoint
// returns `atOrBelow / comparable.length` — a percentile over daily bars. Two
// measured facts make that percentile more precise than the data:
//   1. The independent sample is the YEARS, not the bars. Six prior contract
//      years contributed n=128 bars for CZ26 on 2026-08-28; those bars are
//      autocorrelated inside each year, so "76th percentile" implies a
//      resolution six observations cannot support.
//   2. THE YEARS DO NOT CONTRIBUTE EQUALLY. futures_contract_history stores
//      each expired contract as a ~250-bar one-year segment while a live
//      contract carries its whole trading life. At an offset more than a year
//      before delivery the one-year segments barely reach the window at all:
//      measured on CU27 the same day, 2026 supplied 22 of 57 observations
//      against 6 for 2023. A bar-count percentile silently weights that year
//      nearly four times as heavily.
//
// One observation per year — that year's MEDIAN settle inside the window,
// which is robust to a year contributing 6 bars or 22 — and today ranked
// among them. `nextYearBelow` / `nextYearAbove` are the two adjacent year
// medians, so a caller can state a DETERMINISTIC price at which the ranking
// changes rather than inventing a level.
// A rank among comparable YEARS, as a 0..1 percentile — the fraction of the
// other years this observation sits at or above. rank 1 of 12 -> 1.0, rank 2 of
// 12 -> 10/11. ONE definition, because two callers rounding a rank into a
// percentile differently is how the same contract comes to read 83rd on one
// screen and 86th on the next.
//
// It is deliberately NOT interchangeable with grainContractTech().pctile, which
// ranks a contract against its OWN life-to-date closes. Measured on the live
// board 2026-09-03, that one reads 98.6-100.0 for ALL TWENTY live corn, soybean
// and wheat contracts — a data-shape artifact rather than a market signal,
// because an expired contract is stored as a ~250-bar one-year segment while a
// live one carries its whole trading life (CZ26: 794 bars from 2023-07-10,
// opening in the 2023-24 collapse). The same twenty, ranked among years at the
// same point before delivery, spread 50.0-100.0.
export function yearRankPercentile(rank, totalYears) {
  const r = Number(rank), t = Number(totalYears);
  if (!Number.isFinite(r) || !Number.isFinite(t) || t < 2 || r < 1 || r > t) return null;
  return (t - r) / (t - 1);
}

export function rankContractAmongYears({
  crop, contractMonth, targetContractYear, targetOffsetDays, targetSettle,
  history = [], windowDays = 15, minContractYears = 3,
}) {
  if (targetOffsetDays == null || targetSettle == null || !Number.isFinite(Number(targetSettle))) {
    return { available: false, reason: "no target observation to rank", comparableYears: 0, totalYears: 0, windowDays };
  }
  const comparable = filterComparableFuturesHistory({ crop, contractMonth, targetContractYear, targetOffsetDays, history, windowDays });
  const byYear = new Map();
  for (const r of comparable) {
    const y = Number(r.contractYear);
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y).push(Number(r.settle));
  }
  const years = [...byYear.entries()].map(([year, settles]) => {
    const a = settles.slice().sort((x, y2) => x - y2);
    const mid = Math.floor(a.length / 2);
    const median = a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
    return { year, median, n: a.length };
  }).sort((a, b) => b.median - a.median);

  if (years.length < minContractYears) {
    return {
      available: false,
      reason: years.length
        ? `only ${years.length} comparable contract year(s) within +/-${windowDays}d of this point in contract life (need ${minContractYears})`
        : `no historical ${String(crop || "?").toLowerCase()} ${String(contractMonth || "?")} contract-month archive within +/-${windowDays}d of this point in contract life`,
      comparableYears: years.length, totalYears: years.length ? years.length + 1 : 0, windowDays, years,
    };
  }

  const t = Number(targetSettle);
  const above = years.filter((y) => y.median > t);
  const rank = above.length + 1;               // 1 = highest
  const totalYears = years.length + 1;         // comparable years + today
  return {
    available: true,
    rank, totalYears,
    comparableYears: years.length,
    windowDays,
    targetSettle: t,
    years,
    // The adjacent year medians — the deterministic prices at which this rank
    // changes. Never a round number, never an invented level.
    nextYearAbove: above.length ? above[above.length - 1] : null,
    nextYearBelow: years.find((y) => y.median <= t) || null,
    // Bars actually read, so a reader can see how thin the window was.
    barsRead: comparable.length,
  };
}
